import { useSelector } from 'react-redux'
import { createUrl } from 'src/modules/utils/createUrl'
import { Link } from 'gatsby'
import { selectAnalyticsParameters } from 'src/modules/reducers/analytics'
import SEO from 'src/components/common/SEO'
import { LogoSyncContextProvider } from 'src/components/common/AnnotationContext'
import { fireUIInteractionEvent } from 'src/modules/analytics/v3'
import { selectStateForProtobuf } from 'src/modules/reducers'
import {
  ChannelSection,
  DealSection,
  FAQSection,
  HeroSection,
  Layout,
  PlatformSection,
} from 'src/components/common'
import MultiPPVSection from 'src/components/common/MultiPPVSection'
import { commonCtaText } from 'src/modules/utils/constants'

const translatedCopy = {
  en: {
    channelHeader: 'Included with your Pay Per View purchase',
    channelTitle: '200+ live TV channels you can stream for FREE',
    dealTitle: 'Top reasons to get Pay Per View with Fubo',
    dealSavingsText: '200+ live TV channels included.',
    dealNoContracts: 'Event replays.',
    dealCloudDvr: 'Watch on all your devices.',
    platformHeadline: 'Watch your Pay Per View event from anywhere',
  },
  es: {
    channelHeader: 'Tu compra del Pay Per View incluye:',
    channelTitle: 'Más de 200 canales de TV en vivo que puedes ver GRATIS',
    dealTitle: 'Razones para elegir PPV en Fubo',
    dealSavingsText: 'Más de 200 canales de TV en vivo.',
    dealNoContracts: 'Repeticiones disponibles.',
    dealCloudDvr: 'No necesitas un plan base.',
    platformHeadline: 'Mira TV en vivo y contenido bajo demanda desde cualquier lugar',
  },
}

const PPVTemplate = ({
  lang = 'en',
  market = 'us',
  noRedirect,
  miceText,
  // Offer Code
  offerCode,
  offerBanner,
  // Addon Code
  addonCode,
  // Metadata
  pageTitle,
  pageDescription,
  // Hero Section
  logo,
  logoHeight,
  title,
  subtitle,
  tagline,
  ctaText,
  video,
  videoMobile,
  staticAnnotation,
  annotations,
  poster,
  posterMobile,
  noteLink,
  heroCtaNote,

  // Others
  team,
  // Show offer input for unique codes
  hasOfferInput,
  // AB Test - Video vs Logo Soup
  noVideo = false,
  // Remove references to cable
  removeCable,
  // Charter Plan Cards
  hasCharterPlanCards,

  // Disable Free Trial
  disableFreeTrial,

  // CA Redirect URL
  caRedirectUrl,
  // SVOD Rate Plan
  ratePlan,
  additionalFAQs,
  hasFanduelPlanCards,
  fanduelRSNData,
  dealTitle,
  dealsavingsText,
  dealnoContracts,
  dealcloudDvr,
  faqListOverride,
  hasPlanCards,
  sportId,
  leagueId,
  teamId,
  channelSectionTitle,
  channelTitle,
  platformFootnote,
  platformHeadline,
  platformExcludedDevices,
  heroCTAs,
  isEventEnded = false,
  platformImage,
  heroSideComponent,
  noHeroCTA = false,
  isMultiPPV = false,
  ppvEvents,
  multiPPVOfferCode,
  heroFootnote,
  multiPPVCTA,
  multiPPVTitle,
  multiPPVBody,
}) => {
  let modifiedCtaText = ctaText || (lang === 'es' ? 'Comprar ahora' : commonCtaText.getStarted)

  let metaDescription =
    pageDescription ||
    'Watch Pay Per View events and replays with Fubo. Access 200+ FREE live TV channels to watch any time. Stream on all your devices. No commitment.'

  if (team) {
    // TEMPORARY NBC DISPUTE
    // metaDescription = `Stream ${team} live online with ABC, CBS, FOX, NBC, and ESPN on Fubo. No contract is required - try it for free and cancel anytime.`
    metaDescription = `Stream ${team} live online with ABC, CBS, FOX, and ESPN on Fubo. No contract is required - try it for free and cancel anytime.`
  }

  const stateForProtobuf = useSelector(selectStateForProtobuf)
  const handleLocaleSwitch = () => {
    fireUIInteractionEvent(
      {
        element: noteUrl,
        component: 'lp_locale_switch_clicked',
        type: 'clicked',
      },
      stateForProtobuf
    )
  }

  const originalParams = useSelector(selectAnalyticsParameters)
  let noteUrl
  if (noteLink) {
    noteUrl = createUrl(noteLink, originalParams)
  }

  let noteText =
    lang === 'es' ? (
      <Link to={noteUrl} onClick={handleLocaleSwitch}>
        To learn more in English, <span>click here</span>.
      </Link>
    ) : (
      <Link to={noteUrl} onClick={handleLocaleSwitch}>
        Para traducir a español, <span>haga clic aquí</span>.
      </Link>
    )

  let charterNoteText =
    lang === 'es' ? (
      <Link to={noteUrl} onClick={handleLocaleSwitch}>
        English
      </Link>
    ) : (
      <Link to={noteUrl} onClick={handleLocaleSwitch}>
        Español
      </Link>
    )

  const hasAnchor = hasFanduelPlanCards || hasPlanCards ? '#select-plan' : null

  const hasOffer = offerBanner || offerCode
  if (!hasOffer && fanduelRSNData?.offerCode) {
    offerCode = fanduelRSNData.offerCode
  }

  if (isEventEnded) {
    modifiedCtaText = lang == 'es' ? 'Este evento ha finalizado' : 'This event has ended'
  }

  return (
    <Layout
      lang={lang}
      market={market}
      offerCode={offerCode}
      offerBanner={offerBanner}
      addonCode={addonCode}
      ctaHrefOverride={hasAnchor}
      noteText={noteUrl && hasCharterPlanCards && charterNoteText}
      noRedirect={noRedirect}
      miceText={miceText}
      caRedirectUrl={caRedirectUrl}
      sportId={sportId}
      leagueId={leagueId}
      teamId={teamId}
    >
      <SEO lang={lang} market={market} title={pageTitle || title} description={metaDescription} />
      <LogoSyncContextProvider annotations={annotations}>
        <HeroSection
          logo={logo}
          logoHeight={logoHeight}
          lang={lang}
          market={market}
          heroBgVideo={video}
          heroBgVideoMobile={videoMobile}
          title={title}
          subtitle={subtitle}
          tagline={tagline}
          ctaText={modifiedCtaText}
          staticAnnotation={staticAnnotation}
          posterImg={poster}
          posterImgMobile={posterMobile}
          noteText={heroCtaNote || (noteUrl && !hasCharterPlanCards && noteText)}
          hasOfferInput={hasOfferInput}
          noVideo={noVideo}
          ctaHrefOverride={hasAnchor}
          offerCode={offerCode}
          addonCode={addonCode}
          disableFreeTrial={disableFreeTrial}
          heroCTAs={isEventEnded ? null : heroCTAs}
          isCTADisabled={isEventEnded}
          heroSideComponent={heroSideComponent}
          noCTA={noHeroCTA}
          footnote={heroFootnote || 'Fubo subscription not required'}
        />
      </LogoSyncContextProvider>

      {isMultiPPV && (
        <MultiPPVSection
          lang={lang}
          market={market}
          ctaOfferCode={multiPPVOfferCode}
          ctaText={multiPPVCTA}
          ppvEvents={ppvEvents}
          multiPPVTitle={multiPPVTitle}
          multiPPVBody={multiPPVBody}
        />
      )}

      <ChannelSection
        lang={lang}
        market={market}
        sectionHeaderTitle={channelSectionTitle || translatedCopy[lang].channelHeader}
        title={channelTitle || translatedCopy[lang]?.channelTitle}
        ratePlan={ratePlan}
        fuboFree={true}
      />

      <DealSection
        lang={lang}
        market={'svod'}
        title={dealTitle || translatedCopy[lang].dealTitle}
        savingsText={dealsavingsText || translatedCopy[lang].dealSavingsText}
        noContracts={dealnoContracts || translatedCopy[lang].dealNoContracts}
        cloudDvr={dealcloudDvr || translatedCopy[lang].dealCloudDvr}
      />

      <PlatformSection
        lang={lang}
        market={market}
        ctaText={modifiedCtaText}
        hasOfferInput={hasOfferInput}
        ctaHrefOverride={hasAnchor}
        platformHeadline={platformHeadline || translatedCopy[lang].platformHeadline}
        disableFreeTrial={disableFreeTrial}
        platformFootnote={platformFootnote}
        excludedDevices={platformExcludedDevices}
        isCTADisabled={isEventEnded}
        platformImage={platformImage}
      />

      <FAQSection
        lang={lang}
        market={market}
        team={team}
        removeCable={removeCable}
        additionalFAQs={additionalFAQs}
        isPPV={true}
        faqListOverride={faqListOverride}
      />
    </Layout>
  )
}

export default PPVTemplate
